import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { FormSwitch, Grid, StepControlPanel } from '@/shared/ui';
import { AddressBlock, addressBlockPreview } from '@/shared/ui/AddressBlock';
import { AddressBlockSchema } from '@/shared/ui/AddressBlock/model';

import { IdConstants } from '@/components/Forms/constants';

import { MissingPersonFormData } from '../../model/types';

export const MissingPersonAddress: React.FC<StepProps> = ({ next, prev }) => {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<MissingPersonFormData>();

  useEffect(() => {
    const formData = getValues();

    !!!formData.missingPersonResidenceAddress?.address_equal &&
      setValue('missingPersonResidenceAddress.address_equal', false);
  }, []);

  const { missingPersonRegisteredAddress, missingPersonResidenceAddress } = watch();

  const regAddressBlockValue = useMemo<AddressBlockSchema>(() => {
    const addr = missingPersonRegisteredAddress || {};
    return {
      country: { id: IdConstants.UKRAINE, label: 'Україна' },
      apartment: addr.apartment || null,
      building: addr.building || null,
      district: addr.district || null,
      house: addr.house || null,
      region: addr.region || null,
      settlement: addr.settlement || null,
      street: addr.street || null,
      custom_street: addr.custom_street || null,
      zip_code: addr.zip_code || null,
      // settlement_custom: addr.settlement_custom || null,
      // street_custom: addr.street_custom || null,
    };
  }, [missingPersonRegisteredAddress]);

  const resAddressBlockValue = useMemo<AddressBlockSchema>(() => {
    const addr = missingPersonResidenceAddress || {};
    return {
      country: { id: IdConstants.UKRAINE, label: 'Україна' },
      apartment: addr.apartment || null,
      building: addr.building || null,
      district: addr.district || null,
      house: addr.house || null,
      region: addr.region || null,
      settlement: addr.settlement || null,
      street: addr.street || null,
      custom_street: addr.custom_street || null,
      zip_code: addr.zip_code || null,
      // settlement_custom: addr.settlement_custom || null,
      // street_custom: addr.street_custom || null,
    };
  }, [missingPersonResidenceAddress]);

  const addressEqual = watch('missingPersonResidenceAddress.address_equal');

  return (
    <>
      <FormBlock title="Адреса реєстрації зниклого">
        <AddressBlock
          withCustomStreet
          address={regAddressBlockValue}
          onlyUkraine
          hideCountry
          revalidate={!isEmpty(errors)}
          includeZipCode={false}
          handleAddressChange={(newAddress): void => {
            setValue('missingPersonRegisteredAddress', {
              ...newAddress,
            } as MissingPersonFormData['missingPersonRegisteredAddress']);
          }}
        />
      </FormBlock>
      <FormBlock title="Вкажіть адресу проживання зниклого">
        <FormSwitch
          name="missingPersonResidenceAddress.address_equal"
          control={control}
          label="Адреса реєстрації співпадає з місцем проживання?"
        />
        {!addressEqual && (
          <AddressBlock
            withCustomStreet
            address={resAddressBlockValue}
            hideCountry
            onlyUkraine
            revalidate={!isEmpty(errors)}
            includeZipCode={false}
            handleAddressChange={(newAddress): void => {
              setValue('missingPersonResidenceAddress', {
                ...newAddress,
                address_equal: addressEqual || false,
              } as MissingPersonFormData['missingPersonResidenceAddress']);
            }}
          />
        )}
      </FormBlock>
      <StepControlPanel next={next} prev={prev} />
    </>
  );
};

export const MissingPersonAddressStepPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonFormData>();
  const regAddress = getValues('missingPersonRegisteredAddress');
  const resAddress = getValues('missingPersonResidenceAddress');
  const addressEqual = resAddress?.address_equal;

  return (
    <>
      <Grid
        title="Адреса реєстрації зниклого"
        data={addressBlockPreview({
          address: regAddress,
          includeZipCode: false,
          enableCustomAddressFields: true,
        })}
      />

      <Grid
        title="Адреса проживання зниклого"
        data={[['Адреса реєстрації співпадає з місцем проживання', addressEqual ? 'Так' : 'Ні']]}
      />

      {!addressEqual && (
        <Grid
          title="Вкажіть адресу проживання зниклого"
          data={addressBlockPreview({
            address: resAddress,
            includeZipCode: false,
            enableCustomAddressFields: true,
          })}
        />
      )}
    </>
  );
};
